import { useEffect, useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { Button, Colors, Icon, Text } from '@mercator-gcp/grout';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { ChangePassword } from 'components/modals/ChangePassword';
import { EditAccountDetails } from 'components/modals/EditAccountDetails';
import { AccountSettingsModule } from 'components/widgets/AccountSettings/layout/AccountSettingsModule';
import { AccountSettingsSection } from 'components/widgets/AccountSettings/layout/AccountSettingsSection';
import LinkAccountWithSSO from 'components/widgets/AccountSettings/LinkAccountWithSSO';
import useUserAccount from 'hooks/useUserAccount';
import { useAvailableCities } from 'hooks/useUserCityAccess';
import { useUser } from 'state/auth';
import { trackChangePassword } from 'utils/analytics';
import { ChangePasswordClickActions } from 'utils/analytics/constants';

const styles = {
  modules: {
    gap: 'padding_24',
  },
  label: {
    color: Colors.TextQuiet,
  },
  section: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    py: 'padding_8',
  },
  cityColumn: {
    py: 'padding_8',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 'padding_4',
  },
  cityText: {
    py: 'padding_4',
  },
  verticalSpace: {
    py: 'padding_8',
  },
};

const CHANGE_PASSWORD_BUTTON_TEXT = 'Change password';
const INITIAL_DISPLAYED_CITIES_COUNT = 8;

export const Profile = () => {
  const [isAccountDetailsModalOpen, setIsAccountDetailsModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { user } = useUser();
  const { fetchUser, selectedUser } = useUserAccount();

  // Filter the cities that are allowed
  const { availableCities } = useAvailableCities();

  // Limit displayed markets to initial when not expanded
  const displayedCities = useMemo(() => {
    if (!availableCities) return [];

    return isExpanded
      ? availableCities
      : availableCities.slice(0, INITIAL_DISPLAYED_CITIES_COUNT);
  }, [availableCities, isExpanded]);

  const hasMoreLocations = useMemo(() => {
    return availableCities && availableCities.length > INITIAL_DISPLAYED_CITIES_COUNT;
  }, [availableCities]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    fetchUser(Number(user?.profile_id));
  }, []);

  return (
    <AccountSettingsSection title="Profile">
      <FlexColumn sx={styles.modules}>
        <AccountSettingsModule
          title="Personal Information"
          actions={
            <>
              <Button
                variant="secondary"
                onClick={() => setIsAccountDetailsModalOpen(true)}
                startAdornment={<Icon name={Icon.Name.Pencil} />}
              >
                Edit details
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  trackChangePassword({
                    clickAction: ChangePasswordClickActions.START,
                    clickText: CHANGE_PASSWORD_BUTTON_TEXT,
                  });
                  setIsChangePasswordModalOpen(true);
                }}
                startAdornment={<Icon name={Icon.Name.Pencil} />}
              >
                {CHANGE_PASSWORD_BUTTON_TEXT}
              </Button>
            </>
          }
        >
          <Box sx={styles.verticalSpace}>
            <Text.SmallLabel sx={styles.label}>Email</Text.SmallLabel>
            <Box>{selectedUser?.email}</Box>
          </Box>
          <Box sx={styles.verticalSpace}>
            <Box sx={styles.section}>
              <Box>
                <Text.SmallLabel sx={styles.label}>First Name</Text.SmallLabel>
                <Box>{selectedUser?.first_name}</Box>
              </Box>
              <Box>
                <Text.SmallLabel sx={styles.label}>Last Name</Text.SmallLabel>
                <Box>{selectedUser?.last_name}</Box>
              </Box>
              <Box>
                <Text.SmallLabel sx={styles.label}>Job Title</Text.SmallLabel>
                <Box>{selectedUser?.job_title}</Box>
              </Box>
            </Box>
          </Box>
        </AccountSettingsModule>

        <AccountSettingsModule title="Connect your account">
          <LinkAccountWithSSO />
        </AccountSettingsModule>

        <AccountSettingsModule title="Available Markets">
          <Box sx={styles.cityColumn}>
            {displayedCities.map((city) => (
              <Box sx={styles.cityText} key={city.id}>
                <Text.Body>
                  {city.name}, {city.state}
                </Text.Body>
              </Box>
            ))}
          </Box>
          {hasMoreLocations && (
            <FlexRow sx={{ justifyContent: 'center', marginTop: 'padding_8' }}>
              <Button
                variant="link"
                onClick={toggleExpanded}
                endAdornment={
                  <Icon name={isExpanded ? Icon.Name.ChevronUp : Icon.Name.ChevronDown} />
                }
              >
                {isExpanded ? 'View less' : 'View more'}
              </Button>
            </FlexRow>
          )}
          <Text.Body sx={styles.label}>
            Contact support@mercator.ai to upgrade access for additional markets
          </Text.Body>
        </AccountSettingsModule>
      </FlexColumn>

      <EditAccountDetails
        isOpen={isAccountDetailsModalOpen}
        onClose={() => setIsAccountDetailsModalOpen(false)}
        userData={selectedUser}
        setModalOpen={setIsAccountDetailsModalOpen}
      />
      <ChangePassword
        isOpen={isChangePasswordModalOpen}
        onSave={(clickText: string) =>
          trackChangePassword({
            clickAction: ChangePasswordClickActions.SUBMIT,
            clickText,
          })
        }
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
    </AccountSettingsSection>
  );
};
